import { useEffect, useState } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { wacToken } from "@/libraries/design/foundations";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

import AiEnabledSearchIcon from "./ai-enabled-search-icon";

const searchIconStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: 0,
    cursor: "pointer"
});

const searchIconStyleAI = css({
    justifyContent: "flex-end",
    position: "relative",
    borderRadius: "40px",
    transition: "all 0.5s ease-out",
    zIndex: 2,
    marginInlineStart: "46px",
    padding: "8px"
});

const svgStyle = css({
    "width": "24px",
    "height": "24px",
    "zIndex": "1",
    // Tech debt
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "path": {
        color: wacToken("color.neutral.dark500")
    },
    // Tech debt
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&:hover path": {
        stroke: wacToken("color.primary.blue")
    }
});

const tooltipStyle = css({
    background: wacToken("color.secondary.navy"),
    color: token("color.text.inverse"),
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "600",
    padding: "2px 8px",
    gap: token("space.025")
});

const popoutBackgroundStyle = css({
    position: "absolute",
    insetInlineEnd: "0",
    backgroundColor: wacToken("color.neutral.light100"),
    color: wacToken("color.neutral.dark500"),
    padding: "10px",
    borderRadius: "40px",
    fontSize: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap",
    width: "40px",
    height: "40px",
    zIndex: "-1",
    pointerEvents: "none",
    transition: "width 0.5s ease-in-out"
});

const expandBackgroundStyle = css({
    width: "86px"
});

const fadeInKeyframes = keyframes({
    "0%": {
        opacity: 0
    },
    "40%": {
        opacity: 0
    },
    "100%": {
        opacity: 1
    }
});

const fadeOutKeyframes = keyframes({
    "0%": {
        opacity: 1
    },
    "100%": {
        opacity: 0
    }
});

const popOutTextStyle = css({
    animation: `${fadeOutKeyframes} 0.25s ease-in-out forwards`
});

const popInTextStyle = css({
    animation: `${fadeInKeyframes} 0.5s ease-in-out forwards`
});

const baseTextStyle = css({
    opacity: 0
});

const SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY = "search_ai_animation_played";

const SearchButton = (props: {
    readonly buttonId?: string;
    readonly onClick: React.MouseEventHandler<HTMLButtonElement>;
    aiEnabled?: boolean;
}) => {
    const intl = useIntl();
    const [showSearchPopout, setShowSearchPopout] = useState(false);
    const [hasShownPopout, setHasShownPopout] = useState(false);
    const hasPlayedPopoutAnimation =
        getLocalStorage(SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY) === "true";

    const translated = {
        searchLabel: intl.formatMessage({
            id: "globalnav.v2.search.label",
            defaultMessage: "Search"
        })
    };

    const tooltipText = props.aiEnabled
        ? intl.formatMessage({
              id: "globalnav.v2.search-bar.ai-enabled.tooltip",
              defaultMessage: "Search or ask anything"
          })
        : null;

    useEffect(() => {
        if (props.aiEnabled && !showSearchPopout && !hasPlayedPopoutAnimation) {
            const timeout = setTimeout(() => {
                setShowSearchPopout(true);
                setHasShownPopout(true);
                setLocalStorage(SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY, "true");
                setTimeout(() => {
                    setShowSearchPopout(false);
                }, 2500);
            }, 3000);

            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [props.aiEnabled, showSearchPopout]);

    return (
        <search css={css({ position: "relative" })}>
            {props.aiEnabled && (
                <div
                    css={[
                        popoutBackgroundStyle,
                        showSearchPopout && expandBackgroundStyle
                    ]}
                >
                    <div
                        css={[
                            baseTextStyle,
                            showSearchPopout && popInTextStyle,
                            !showSearchPopout &&
                                hasShownPopout &&
                                popOutTextStyle
                        ]}
                    >
                        Search
                    </div>
                </div>
            )}
            <Tooltip content={tooltipText} css={tooltipStyle}>
                <button
                    css={[
                        searchIconStyle,
                        props.aiEnabled && searchIconStyleAI
                    ]}
                    onClick={props.onClick}
                    id={props.buttonId}
                    aria-label={String(translated.searchLabel).toLowerCase()}
                    data-testid="global-nav-search-icon"
                    data-event="clicked"
                    data-event-component="linkButton"
                    data-event-container="globalNavWac"
                    data-product-key="jiraSoftware"
                    data-ai-enabled={props.aiEnabled}
                >
                    {props.aiEnabled ? (
                        <AiEnabledSearchIcon css={css({ zIndex: "3" })} />
                    ) : (
                        <svg
                            css={svgStyle}
                            width="23"
                            height="25"
                            viewBox="0 0 23 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M17.0918 18.5623L20.2497 21.7201"
                                stroke="#101214"
                                strokeWidth="2.75"
                                strokeLinecap="round"
                            />
                            <path
                                d="M10.8417 19.1316C15.289 19.1316 18.8943 15.5263 18.8943 11.079C18.8943 6.63165 15.289 3.02637 10.8417 3.02637C6.39435 3.02637 2.78906 6.63165 2.78906 11.079C2.78906 15.5263 6.39435 19.1316 10.8417 19.1316Z"
                                stroke="#101214"
                                strokeWidth="2.25"
                            />
                        </svg>
                    )}
                </button>
            </Tooltip>
        </search>
    );
};

export default SearchButton;
